/**
 * Таймер самоуничтожения (изменение v1.13, roadmap F16 для секретных чатов).
 *
 * Отсчёт взводится при прочтении (`messagesRepo.armSelfDestruct`), а здесь истёкшие
 * сообщения убираются из реплики и — лучшим усилием — с сервера: удаление «у всех»
 * доступно в секретном чате любому участнику, поэтому первое сработавшее устройство
 * стирает конверт и у собеседника. Защита от снимков экрана не обещается.
 *
 * Сборщик тикает раз в секунду, но в базу ходит лениво: IndexedDB в WebKit дорог, и
 * запрос каждую секунду в каждой вкладке замедлял всё приложение. Поэтому запоминается
 * ближайший срок, а полный проход делается, когда он подошёл, когда прочтение взвело
 * новый таймер (`noteSecretArmed`) или раз в `SECRET_RESCAN_MS` — таймер могла взвести
 * другая вкладка.
 */
import { uuid } from '@shared/lib/uuid'
import { PENDING_SEQ_BASE, messagesRepo, queueRepo, type DeleteMessageOp } from '@shared/db'

/** Как часто перепроверять базу без сигнала: таймер могла взвести другая вкладка. */
export const SECRET_RESCAN_MS = 15_000

/** Ближайший известный срок (мс эпохи); 0 — неизвестен, ∞ — взведённых нет. */
let dueAt = 0
let scannedAt = 0

/** Прочтение взвело таймер в этой вкладке — следующий тик заглянет в базу сразу. */
export function noteSecretArmed(): void {
  dueAt = 0
}

/** Тик сборщика: полный проход только когда срок подошёл, неизвестен или пора пересканировать. */
export async function sweepSecretMessagesIfDue(now = Date.now()): Promise<number> {
  if (dueAt > now && now - scannedAt < SECRET_RESCAN_MS) return 0
  scannedAt = now
  const removed = await sweepExpiredSecretMessages(now)
  dueAt = (await messagesRepo.nextSecretExpiry()) ?? Number.POSITIVE_INFINITY
  return removed
}

export async function sweepExpiredSecretMessages(now = Date.now()): Promise<number> {
  const expired = await messagesRepo.expiredSecretMessages(now)
  for (const row of expired) {
    await messagesRepo.deleteLocally(row.conversation_id, row.seq)
    // Неподтверждённой записи на сервере ещё нет, а tombstone уже стёрт обеими сторонами.
    if (row.deleted || row.seq >= PENDING_SEQ_BASE) continue
    const operation: DeleteMessageOp = {
      op_id: uuid(),
      kind: 'delete_message',
      created_at: new Date().toISOString(),
      attempt_count: 0,
      next_attempt_at: null,
      last_error: null,
      claimed_by: null,
      claimed_at: null,
      idempotency_key: null,
      conversation_id: row.conversation_id,
      message_id: row.id,
      scope: 'all',
    }
    await queueRepo.enqueue(operation)
  }
  return expired.length
}
